import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, OnInit, Output, input, signal, effect, Renderer2, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigBirthDate } from '../../interfaces/interfaces';

@Component({
  selector: 'app-birthdate',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, CommonModule],
  templateUrl: './birthdate.component.html',
  styleUrl: './birthdate.component.css'
})
export class BirthdateComponent implements OnInit, AfterViewInit, OnDestroy {

  config = input<ConfigBirthDate>();

  @Output() submit: EventEmitter<any> = new EventEmitter<any>();

  form: FormGroup;
  
  // Señales para control de errores
  showRequiredError = signal(false);
  showAgeError = signal(false);
  hasAgeError = signal(false); // Indica si hay error de edad (para lógica interna)
  isInactiveError = signal(false); // Indica si el error es por inactividad
  
  // Nueva señal para controlar si el formulario ya tiene valores por defecto válidos
  private hasValidDefaultValue = signal(false);
  
  months = [
    { value: 1, name: 'Enero' }, { value: 2, name: 'Febrero' },
    { value: 3, name: 'Marzo' }, { value: 4, name: 'Abril' },
    { value: 5, name: 'Mayo' }, { value: 6, name: 'Junio' },
    { value: 7, name: 'Julio' }, { value: 8, name: 'Agosto' },
    { value: 9, name: 'Septiembre' }, { value: 10, name: 'Octubre' },
    { value: 11, name: 'Noviembre' }, { value: 12, name: 'Diciembre' },
  ];

  allowedYears: number[] = [];
  daysInMonth: number[] = [];

  // Control de timeout
  private requiredErrorTimer: any;
  private ageValidationTimer: any;
  private viewInitialized = signal(false);
  private lastInteractionTime = 0; // Para controlar inactividad
  private interactionCheckInterval: any;

  @ViewChild('formContainer') formContainer!: ElementRef;
  @ViewChild('labelElement') labelElement!: ElementRef;

  constructor(private renderer: Renderer2){
    this.form = new FormGroup({
      year: new FormControl('', Validators.required),
      month: new FormControl('', Validators.required),
      day: new FormControl('', Validators.required),
    });

    this.generateAllowedYears();

    // Efecto principal para configuración
    effect(() => {
      const cfg = this.config();
      const enabled = cfg?.enabled ?? true;
      
      if (enabled) {
        this.form.enable();
        this.startInactivityMonitoring();
      } else {
        // IMPORTANTE: Cuando está deshabilitado, detener todo monitoreo y limpiar errores
        this.form.disable();
        this.stopInactivityMonitoring();
        this.clearAllErrors();
        this.clearTimers();
        this.getStyleTextDisabled();
        
        // Si hay defaultValue y está deshabilitado, emitir el valor directamente
        if (cfg?.defaultValue && this.isValidDefaultValue(cfg.defaultValue)) {
          this.hasValidDefaultValue.set(true);
          this.submit.emit(this.form.value);
          console.log('Componente deshabilitado con defaultValue válido, emitiendo:', this.form.value);
        }
      }
    });

    // Efecto para valor por defecto (se ejecuta independientemente del estado enabled)
    effect(() => {
      const cfg = this.config();
      if (cfg?.defaultValue) {
        // Procesar defaultValue siempre, sin importar el estado enabled
        // Esto asegura que los valores se muestren en los selects
        this.processDefaultValue(cfg.defaultValue, cfg?.enabled ?? true);
      }
    });

    // Suscripción a cambios del formulario
    this.form.valueChanges.subscribe(() => {
      this.handleFormChanges();
    });
  }

  ngOnInit() {
    this.onMonthOrYearChange();
  }

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();
    
    // Forzar actualización de días para defaultValue si existe
    const cfg = this.config();
    if (cfg?.defaultValue) {
      setTimeout(() => {
        this.updateDaysForDefaultValue(cfg.defaultValue!);
      }, 0);
    }
  }

  ngOnDestroy() {
    this.clearTimers();
    this.stopInactivityMonitoring();
  }

  // ========== MÉTODOS AUXILIARES ==========

  private isValidDefaultValue(defaultValue: {day: number, month: number, year: number}): boolean {
    const birthDate = new Date(defaultValue.year, defaultValue.month - 1, defaultValue.day);
    return this.isUserOfLegalAge(birthDate);
  }

  private processDefaultValue(defaultValue: {day: number, month: number, year: number}, isEnabled: boolean): void {
    console.log('Procesando defaultValue:', defaultValue, 'enabled:', isEnabled);
    
    // Establecer valores en el formulario
    this.form.patchValue({
      year: defaultValue.year,
      month: defaultValue.month,
      day: defaultValue.day
    }, { emitEvent: false });
    
    // Actualizar días disponibles para el mes/año
    this.updateDaysForDefaultValue(defaultValue);
    
    // Si está habilitado, realizar validaciones adicionales
    if (isEnabled) {
      this.hasValidDefaultValue.set(false);
      this.resetInactivityTimer();
      
      // Validar edad del valor por defecto después de un breve delay
      setTimeout(() => {
        this.validateAgeForDefaultValue();
      }, 100);
    } else {
      // Si está deshabilitado, marcar como válido y emitir
      const birthDate = new Date(defaultValue.year, defaultValue.month - 1, defaultValue.day);
      if (this.isUserOfLegalAge(birthDate)) {
        this.hasValidDefaultValue.set(true);
        this.submit.emit(this.form.value);
        console.log('Componente deshabilitado con defaultValue válido, emitiendo:', this.form.value);
      }
    }
  }

  private updateDaysForDefaultValue(defaultValue: {day: number, month: number, year: number}): void {
    // Calcular días disponibles para el mes/año especificado
    if (defaultValue.month && defaultValue.year) {
      const days = new Date(defaultValue.year, defaultValue.month, 0).getDate();
      this.daysInMonth = Array.from({ length: days }, (_, i) => i + 1);
      console.log('Días disponibles para', defaultValue.year, '-', defaultValue.month, ':', this.daysInMonth);
    }
  }

  private clearAllErrors(): void {
    this.showRequiredError.set(false);
    this.showAgeError.set(false);
    this.hasAgeError.set(false);
    this.isInactiveError.set(false);
  }

  // ========== VALIDACIÓN ESPECIAL PARA DEFAULT VALUE ==========
  
  private validateAgeForDefaultValue(): void {
    if (!this.form.valid) {
      this.hasValidDefaultValue.set(false);
      return;
    }

    const year = this.form.get('year')?.value;
    const month = this.form.get('month')?.value;
    const day = this.form.get('day')?.value;

    if (year && month && day) {
      const birthDate = new Date(year, month - 1, day);
      const isOfLegalAge = this.isUserOfLegalAge(birthDate);
      
      console.log('Validando defaultValue - Fecha:', birthDate, 'Es mayor de edad:', isOfLegalAge);
      
      if (!isOfLegalAge) {
        // Si el defaultValue no es válido (menor de edad)
        this.showAgeError.set(true);
        this.hasAgeError.set(true);
        this.hasValidDefaultValue.set(false);
        this.getStyleError();
        this.emitNull();
        console.log('DefaultValue inválido (menor de edad), emitiendo null');
      } else {
        // Si el defaultValue es válido (mayor de edad)
        this.showAgeError.set(false);
        this.hasAgeError.set(false);
        this.hasValidDefaultValue.set(true);
        this.getStyleEnabled();
        
        // Detener monitoreo de inactividad ya que tenemos un valor válido
        this.stopInactivityMonitoring();
        
        // Emitir el valor solo si es mayor de edad
        this.submit.emit(this.form.value);
        console.log('DefaultValue válido, se emite el valor:', this.form.value);
      }
    }
  }

  // ========== MANEJO DE INACTIVIDAD ==========

  private startInactivityMonitoring(): void {
    // Solo iniciar monitoreo si el componente está habilitado y no hay un defaultValue válido
    const cfg = this.config();
    if (cfg?.enabled === false || this.hasValidDefaultValue()) {
      return;
    }
    
    // Resetear tiempo de interacción inicial
    this.lastInteractionTime = Date.now();
    
    // Configurar event listeners para interacciones del usuario
    this.setupInteractionListeners();
    
    // Iniciar chequeo periódico de inactividad
    this.interactionCheckInterval = setInterval(() => {
      this.checkInactivity();
    }, 1000); // Verificar cada segundo
  }

  private stopInactivityMonitoring(): void {
    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }
  }

  private setupInteractionListeners(): void {
    // Solo configurar listeners si el componente está habilitado
    const cfg = this.config();
    if (cfg?.enabled === false) {
      return;
    }
    
    // Escuchar interacciones en los selects
    if (this.formContainer?.nativeElement) {
      const selects = this.formContainer.nativeElement.querySelectorAll('select');
      selects.forEach((select: HTMLElement) => {
        this.renderer.listen(select, 'change', () => this.resetInactivityTimer());
        this.renderer.listen(select, 'focus', () => this.resetInactivityTimer());
        this.renderer.listen(select, 'click', () => this.resetInactivityTimer());
      });
    }
  }

  public resetInactivityTimer(): void {
    // Solo resetear si el componente está habilitado
    const cfg = this.config();
    if (cfg?.enabled === false) {
      return;
    }
    
    this.lastInteractionTime = Date.now();
    
    // Si había error por inactividad, limpiarlo
    if (this.isInactiveError()) {
      this.isInactiveError.set(false);
      // Si no hay otros errores, restaurar estilo normal
      if (!this.hasAgeError() && this.form.valid) {
        this.getStyleEnabled();
      }
    }
  }

  private checkInactivity(): void {
    // No chequear inactividad si el componente está deshabilitado o ya hay un defaultValue válido
    const cfg = this.config();
    if (cfg?.enabled === false || this.hasValidDefaultValue()) {
      return;
    }
    
    const currentTime = Date.now();
    const inactiveTime = currentTime - this.lastInteractionTime;
    const inactivityThreshold = 8000; // 8 segundos en milisegundos

    // Si ha pasado más de 8 segundos sin interacción Y el formulario no es válido
    if (inactiveTime >= inactivityThreshold && !this.form.valid) {
      // Solo mostrar error si no está ya mostrando
      if (!this.showRequiredError() || !this.isInactiveError()) {
        this.isInactiveError.set(true);
        this.showRequiredError.set(true);
        this.getStyleError();
        this.emitNull(); // Emitir null por inactividad
      }
    }
  }

  // ========== LÓGICA PRINCIPAL ==========

  private handleFormChanges(): void {
    // Solo procesar cambios si el componente está habilitado
    const cfg = this.config();
    if (cfg?.enabled === false) {
      return;
    }
    
    // Resetear timer de inactividad
    this.resetInactivityTimer();
    
    // Limpiar timer de error requerido cuando el usuario empieza a escribir
    if (this.form.touched || this.form.dirty) {
      this.clearRequiredErrorTimer();
    }

    if (this.form.valid) {
      // El formulario está completo, validar edad
      this.showRequiredError.set(false);
      this.isInactiveError.set(false);
      this.validateAgeWithDebounce();
    } else {
      // El formulario no está completo
      this.showAgeError.set(false);
      this.hasAgeError.set(false);
      this.hasValidDefaultValue.set(false); // Resetear flag de defaultValue válido
      this.getStyleEnabled(); // Restaurar estilo normal
      
      // Emitir null cuando el formulario no es válido
      this.emitNull();
      
      // Programar mensaje de error requerido si lleva tiempo vacío
      if (!this.form.touched && !this.form.dirty) {
        this.startRequiredErrorTimer();
      }
    }
  }

  private validateAgeWithDebounce(): void {
    // Limpiar timer previo
    if (this.ageValidationTimer) {
      clearTimeout(this.ageValidationTimer);
    }
    
    // Validar con debounce para evitar validaciones muy frecuentes
    this.ageValidationTimer = setTimeout(() => {
      this.validateAge();
    }, 300);
  }

  private validateAge(): void {
    // Solo validar si el componente está habilitado
    const cfg = this.config();
    if (cfg?.enabled === false || !this.form.valid) return;

    const year = this.form.get('year')?.value;
    const month = this.form.get('month')?.value;
    const day = this.form.get('day')?.value;

    if (year && month && day) {
      const birthDate = new Date(year, month - 1, day);
      const isOfLegalAge = this.isUserOfLegalAge(birthDate);
      
      console.log('Fecha de nacimiento:', birthDate);
      console.log('Es mayor de edad:', isOfLegalAge);
      
      if (!isOfLegalAge) {
        // MENOR DE EDAD: Aplicar y mantener error
        this.showAgeError.set(true);
        this.hasAgeError.set(true);
        this.hasValidDefaultValue.set(false); // Resetear flag
        this.getStyleError();
        this.emitNull(); // Emitir null por edad inválida
        
        // Reiniciar monitoreo de inactividad
        this.startInactivityMonitoring();
        
        console.log('Usuario menor de edad, no se emite el valor');
      } else {
        // MAYOR DE EDAD: Limpiar errores y aplicar estilo normal
        this.showAgeError.set(false);
        this.hasAgeError.set(false);
        this.hasValidDefaultValue.set(true); // Marcar como valor válido
        this.getStyleEnabled();
        
        // Detener monitoreo de inactividad ya que tenemos un valor válido
        this.stopInactivityMonitoring();
        
        // Emitir el valor solo si es mayor de edad
        this.submit.emit(this.form.value);
        console.log('Usuario mayor de edad, se emite el valor:', this.form.value);
      }
    }
  }

  private emitNull(): void {
    // Solo emitir null si el componente está habilitado
    const cfg = this.config();
    if (cfg?.enabled === false) {
      return;
    }
    
    // Emitir null cuando hay algún error o el formulario no es válido
    this.submit.emit(null);
    console.log('Emitting null due to invalid state or inactivity');
  }

  isUserOfLegalAge(birthDate: Date): boolean {
    const today = new Date();
    
    // Calcular edad
    let age = today.getFullYear() - birthDate.getFullYear();
    
    // Ajustar si aún no ha cumplido años este año
    const hasHadBirthdayThisYear = 
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
    
    if (!hasHadBirthdayThisYear) {
      age--;
    }
    
    return age >= 18;
  }

  // ========== CONTROL DE TIMERS ==========

  private startRequiredErrorTimer(): void {
    // No iniciar timer si el componente está deshabilitado o ya hay un defaultValue válido
    const cfg = this.config();
    if (cfg?.enabled === false || this.hasValidDefaultValue()) {
      return;
    }
    
    this.clearRequiredErrorTimer();
    
    this.requiredErrorTimer = setTimeout(() => {
      if (!this.form.valid && (this.form.touched || this.form.dirty)) {
        this.showRequiredError.set(true);
        this.getStyleError();
        this.emitNull(); // Emitir null cuando se muestra error requerido
      }
    }, 5000); // 5 segundos
  }

  private clearRequiredErrorTimer(): void {
    if (this.requiredErrorTimer) {
      clearTimeout(this.requiredErrorTimer);
      this.requiredErrorTimer = null;
    }
  }

  private clearTimers(): void {
    this.clearRequiredErrorTimer();
    
    if (this.ageValidationTimer) {
      clearTimeout(this.ageValidationTimer);
      this.ageValidationTimer = null;
    }
  }

  // ========== MANEJO DE MESES Y DIAS ==========

  generateAllowedYears() {
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 100; // Permitir hasta 100 años atrás
    const maxYear = currentYear - 18; // Año máximo para ser mayor de edad
    
    for (let year = maxYear; year >= minYear; year--) {
      this.allowedYears.push(year);
    }
  }

  onMonthOrYearChange() {
    // Solo procesar cambios si el componente está habilitado
    const cfg = this.config();
    if (cfg?.enabled === false) {
      return;
    }
    
    const month = this.form.get('month')?.value;
    const year = this.form.get('year')?.value;

    if (month && year) {
      const days = new Date(year, month, 0).getDate();
      this.daysInMonth = Array.from({ length: days }, (_, i) => i + 1);

      const currentDay = this.form.get('day')?.value;
      if (currentDay > days) {
        this.form.get('day')?.setValue('');
      }
    } else {
      this.daysInMonth = [];
    }
    
    // Al cambiar mes o año, resetear flag de defaultValue válido
    this.hasValidDefaultValue.set(false);
    
    // Reiniciar monitoreo de inactividad cuando el usuario interactúa
    this.startInactivityMonitoring();
    
    // Al cambiar mes o año, limpiar error de edad temporalmente
    // Se validará de nuevo cuando el formulario esté completo
    if (this.hasAgeError()) {
      this.showAgeError.set(false);
      this.hasAgeError.set(false);
      this.getStyleEnabled();
    }
    
    // Resetear inactividad cuando el usuario interactúa
    this.resetInactivityTimer();
  }

  // ========== ESTILOS ==========

  private applyInitialStyles() {
    const cfg = this.config();
    if (cfg?.enabled === false) {
      this.getStyleTextDisabled();
    } else {
      this.getStyleEnabled();
    }
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  getStyleError() {
    // No aplicar estilo de error si el componente está deshabilitado
    const cfg = this.config();
    if (cfg?.enabled === false) {
      return;
    }
    
    this.safeApplyStyles(() => {
      // Aplicar estilo de error al label
      if (this.labelElement?.nativeElement) {
        this.renderer.removeClass(this.labelElement.nativeElement, 'text-label');
        this.renderer.removeClass(this.labelElement.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.labelElement.nativeElement, 'text-label-error');
      }

      // Aplicar estilo de error al contenedor de selects
      if (this.formContainer?.nativeElement) {
        const selectsContainer = this.formContainer.nativeElement.querySelector('div.flex');
        if (selectsContainer) {
          this.renderer.removeClass(selectsContainer, 'border-blue-800');
          this.renderer.addClass(selectsContainer, 'border-red-500');
        }
        
        // Aplicar estilo a los selects individuales
        const selects = this.formContainer.nativeElement.querySelectorAll('select');
        selects.forEach((select: HTMLElement) => {
          this.renderer.removeClass(select, 'border-blue-800');
          this.renderer.addClass(select, 'border-red-500');
        });
      }
    });
  }
  
  getStyleEnabled() {
    // Solo aplicar estilo normal si el componente está habilitado
    const cfg = this.config();
    if (cfg?.enabled === false) {
      return;
    }
    
    this.safeApplyStyles(() => {
      // Solo aplicar estilo normal si NO hay error de edad
      if (!this.hasAgeError() && !this.isInactiveError()) {
        // Restaurar estilo normal al label
        if (this.labelElement?.nativeElement) {
          this.renderer.removeClass(this.labelElement.nativeElement, 'text-label-error');
          this.renderer.removeClass(this.labelElement.nativeElement, 'text-label-disabled');
          this.renderer.addClass(this.labelElement.nativeElement, 'text-label');
        }

        // Restaurar estilo normal al contenedor de selects
        if (this.formContainer?.nativeElement) {
          const selectsContainer = this.formContainer.nativeElement.querySelector('div.flex');
          if (selectsContainer) {
            this.renderer.removeClass(selectsContainer, 'border-red-500');
            this.renderer.addClass(selectsContainer, 'border-blue-800');
          }
          
          // Restaurar estilos a los selects individuales
          const selects = this.formContainer.nativeElement.querySelectorAll('select');
          selects.forEach((select: HTMLElement) => {
            this.renderer.removeClass(select, 'border-red-500');
            this.renderer.addClass(select, 'border-blue-800');
          });
        }
      }
    });
  }

  getStyleTextDisabled() {
    this.safeApplyStyles(() => {
      if (this.labelElement?.nativeElement) {
        this.renderer.removeClass(this.labelElement.nativeElement, 'text-label');
        this.renderer.removeClass(this.labelElement.nativeElement, 'text-label-error');
        this.renderer.addClass(this.labelElement.nativeElement, 'text-label-disabled');
      }
    });
  }

  // ========== SUBMIT ==========

  onSubmit() {
    // Solo procesar submit si el componente está habilitado
    const cfg = this.config();
    if (cfg?.enabled === false) {
      return;
    }
    
    if (this.form.valid && !this.hasAgeError()) {
      this.submit.emit(this.form.value);
    } else if (this.hasAgeError()) {
      // Si hay error de edad, asegurarse de que se muestre el estilo de error
      this.getStyleError();
      this.emitNull();
    } else {
      // Si el formulario no es válido, emitir null
      this.emitNull();
    }
  }
}